import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AdminApi, ApiError, SessionExpired, type AdminMe, type AdminSignInConfig, type DevAdmin } from './api.js';
import {
  beginSignIn,
  completeSignIn,
  endSessionUrl,
  OidcError,
  type OidcFailure,
} from './oidc.js';

/**
 * Who is signed in, and the one place that decides they are not any more.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE TOKEN LIVES IN sessionStorage, NOT localStorage
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The same choice the product console makes for its PKCE values, for a reason
 * that applies far more strongly here: this token can destroy every customer's
 * books. `localStorage` survives the browser closing, so a token on a shared
 * or stolen laptop stays valid until it expires. `sessionStorage` is per tab
 * and dies with it.
 *
 * The stronger reason is that it costs nothing. An admin signs in a few times
 * a week; the convenience being traded away is imperceptible, and the thing
 * bought is that a closed tab is a closed session.
 *
 * ── AND THE PLATFORM IS STILL THE AUTHORITY ───────────────────────────────
 *
 * Holding a token here means nothing on its own. Every request re-reads the
 * session row server-side, so a session closed from another tab, an admin
 * suspended, or an expiry passing all take effect on the next call — and
 * arrive here as a 401 that clears this state. The token is a convenience;
 * the row is the truth.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY SIGN-IN IS NEVER AUTOMATIC HERE, UNLIKE THE PRODUCT CONSOLE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The product console redirects to the provider the moment it finds no token:
 * everyone who opens it is meant to be signed in, so an extra click is friction
 * with no purpose.
 *
 * This console does the opposite and waits to be asked. Somebody who lands here
 * by mistake, or by following a link, should see what this console IS before
 * their browser is sent anywhere. And an automatic redirect on load turns a
 * page that fails to render into an infinite bounce through the provider, which
 * is a hard failure to read from either end.
 */

const TOKEN_KEY = 'cosmos.admin.token';
const CALLBACK_PATH = '/auth/callback';

/** Why a sign-in did not finish, as a key rather than a sentence. */
export type SignInFailure = OidcFailure | 'not-an-admin' | 'platform-unreachable';

export interface SessionState {
  /** Null until signed in. */
  me: AdminMe | null;
  /** True while the stored token is being checked on first load. */
  loading: boolean;
  api: AdminApi;
  /**
   * Null when the platform has no admin surface at all. The sign-in screen
   * renders a reason rather than a control in that case.
   *
   * `{ mode: 'dev' }` is NOT that: it means the platform has an admin surface
   * and no identity provider, which is a laptop, and the screen offers a
   * picker. Conflating the two is what produced a screen showing "Nothing
   * answered" and "Sign-in is not available here" at the same time.
   */
  signInConfig: AdminSignInConfig | null;
  /** Identities a dev sign-in may pick. Empty unless the platform is in dev mode. */
  devAdmins: DevAdmin[];
  /** Dev mode only: sign in as one of them. */
  signInAs: (email: string) => Promise<void>;
  /** Non-null when a sign-in failed and the person needs to be told why. */
  signInError: SignInFailure | null;
  /** Starts the redirect to the provider. Does not return. */
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

/**
 * Leave the callback URL without a full reload.
 *
 * `/auth/callback` is not a page. Left in the address bar, a refresh replays a
 * callback whose code has already been redeemed, and the person sees a sign-in
 * failure for something that succeeded.
 */
function leaveCallback(): void {
  window.history.replaceState(null, '', '/');
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState<AdminMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [signInConfig, setSignInConfig] = useState<AdminSignInConfig | null>(null);
  const [devAdmins, setDevAdmins] = useState<DevAdmin[]>([]);
  const [signInError, setSignInError] = useState<SignInFailure | null>(null);

  const api = useMemo(() => new AdminApi(token), [token]);

  const forget = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMe(null);
  }, []);

  /*
    Boot, in one pass: what the platform offers, then the callback leg if this
    IS one, then the stored token.

    The callback is handled BEFORE the stored token is trusted, because arriving
    with a code means a sign-in is in progress and any stored token belongs to
    whoever was signed in before.
  */
  useEffect(() => {
    let live = true;

    void (async () => {
      const config = await AdminApi.signInConfig();
      if (!live) return;
      setSignInConfig(config);

      if (config?.mode === 'dev') {
        // Best effort. A picker that cannot be filled is a worse screen than
        // one that says the platform is unreachable, and this failing does not
        // mean the platform is.
        setDevAdmins(await AdminApi.devAdmins().catch(() => []));
      }

      if (window.location.pathname === CALLBACK_PATH) {
        /*
          A callback with no configuration cannot be completed: the token
          endpoint is discovered from the issuer, and there is no issuer. It
          means the platform's admin surface went away mid-sign-in, which is
          rare and not something a retry fixes.
        */
        if (config?.mode !== 'oidc') {
          setSignInError('platform-unreachable');
          leaveCallback();
          setLoading(false);
          return;
        }
        try {
          const { idToken } = await completeSignIn(config, window.location.search);
          const grant = await AdminApi.exchange(idToken);
          if (!live) return;
          sessionStorage.setItem(TOKEN_KEY, grant.token);
          setToken(grant.token);
        } catch (err) {
          if (!live) return;
          /*
            403 is its own case and must not be collapsed into "sign-in
            failed". It means the provider verified them and this platform does
            not consider them an administrator — so the advice is not "try
            again", which would loop them through a sign-in that keeps
            succeeding.
          */
          if (err instanceof ApiError && err.status === 403) setSignInError('not-an-admin');
          else if (err instanceof OidcError) setSignInError(err.code);
          else setSignInError('exchange-failed');
        } finally {
          if (live) {
            leaveCallback();
            setLoading(false);
          }
        }
        return;
      }

      if (!token) {
        setMe(null);
        setLoading(false);
        return;
      }

      /*
        A stored token is CHECKED, never trusted.

        The console asks the platform who this session belongs to. A token that
        survived a suspension, a sign-out in another tab, or an expiry is
        refused there and cleared here — so the console never renders a
        signed-in shell around a session that no longer exists.
      */
      try {
        const who = await api.me();
        if (live) setMe(who);
      } catch (err) {
        if (!live) return;
        if (err instanceof SessionExpired) forget();
        else setMe(null);
      } finally {
        if (live) setLoading(false);
      }
    })().catch(() => {
      if (!live) return;
      // The platform itself did not answer. A person cannot act on which call
      // failed, so it gets one message rather than a description of plumbing.
      setSignInError('platform-unreachable');
      setLoading(false);
    });

    return () => {
      live = false;
    };
  }, [api, token, forget]);

  const value = useMemo<SessionState>(
    () => ({
      me,
      loading,
      api,
      signInConfig,
      devAdmins,
      signInError,
      signInAs: async (email: string) => {
        setSignInError(null);
        try {
          const grant = await AdminApi.devToken(email);
          sessionStorage.setItem(TOKEN_KEY, grant.token);
          setToken(grant.token);
        } catch (err) {
          // The roster still decides in dev mode, so 403 here means the same
          // thing it means in production and gets the same words.
          setSignInError(err instanceof ApiError && err.status === 403 ? 'not-an-admin' : 'exchange-failed');
        }
      },
      signIn: async () => {
        if (signInConfig?.mode !== 'oidc') return;
        setSignInError(null);
        /*
          No `returnTo`. The product console remembers where somebody was going
          because it deep-links; this console has one page, and carrying a
          return path would be a stored value with no reader — the kind of thing
          that becomes an open redirect the day somebody starts using it.
        */
        await beginSignIn(signInConfig).catch((err: unknown) => {
          setSignInError(err instanceof OidcError ? err.code : 'platform-unreachable');
        });
      },
      signOut: async () => {
        /*
          Tell the platform FIRST, then forget locally.

          The order matters: clearing the token first would leave the session
          row open on the server with nothing able to close it — a session
          nobody is holding but which would still authorise anything that
          obtained the token. The catch is deliberate: if the call fails the
          local token is still cleared, because a sign-out that refuses to
          sign you out is worse than one that leaves a row to expire.
        */
        await api.logout().catch(() => undefined);
        forget();

        /*
          And end the PROVIDER's session, which neither step above touches.

          Without this the provider stays signed in, so the next person at this
          machine reaches an administrator session in one click and is never
          asked for a password. On a console whose own sign-in screen says it
          can read and destroy any customer account, that is the wrong default.

          Last, and never instead of the two above: if the provider cannot be
          reached, or publishes no end-session endpoint, this browser and the
          platform session are already clear.
        */
        if (signInConfig?.mode === 'oidc') {
          const url = await endSessionUrl(
            {
              issuer: signInConfig.issuer,
              clientId: signInConfig.clientId,
              scopes: signInConfig.scopes,
            },
            `${window.location.origin}/`,
          );
          if (url) window.location.assign(url);
        }
      },
    }),
    [me, loading, api, forget, signInConfig, signInError, devAdmins],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(Ctx);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
